import { useEffect, useState } from "react";
import { ChoiceCard } from "@core/components/ChoiceCard";
import { List, ListHeading } from "@core/components/List";
import { LongText } from "@core/components/LongText";
import type { OpenChoice } from "@core/api/types";
import type { StepProps } from "@core/registry";
import { CultureMap, CultureMapButton, CultureMapSheet, RegionMapButton, RegionMapSheet } from "./CultureMap";
import { AllocationEditor, ModuleCards } from "./parts";
import { joinOr, page, signed, useSm, type CatalogEntry, type CultureEntry, type RegionEntry, type SlotView, type StepView } from "./view";

const stepKeys = { kultur: "culture", abstammung: "ancestry", ausbildung: "education" } as const;
const stepNames = { kultur: "Kultur", abstammung: "Abstammung", ausbildung: "Ausbildung" } as const;
type ModuleStepId = keyof typeof stepKeys;

const categories: { id: SlotView["category"]; label: string }[] = [
  { id: "staerke", label: "Stärken" },
  { id: "ressource", label: "Ressourcen" },
  { id: "fertigkeit", label: "Fertigkeiten" },
  { id: "meisterschaft", label: "Meisterschaften" },
  { id: "sprache", label: "Sprachen" },
];

/** Culture, ancestry and education: pick a module, then work through its slots one decision at a time, or distribute freely. */
export function ModuleStep({ step }: StepProps) {
  const { view, confirm, apply, character, language } = useSm();
  const id = step as ModuleStepId;
  const current = view[stepKeys[id]] as StepView | null;
  const [choosing, setChoosing] = useState(current === null);
  useEffect(() => {
    if (current === null) setChoosing(true);
  }, [current === null]);

  if (choosing || !current) {
    return <ModulePicker step={id} current={current} onDone={() => setChoosing(false)} />;
  }

  const stepChoices = character.evaluation.openChoices.filter((c) => c.step === step);
  const education = id === "ausbildung" ? view.catalog?.educations.find((e) => e.id === current.id) : undefined;
  const culture = id === "kultur" ? view.catalog?.cultures.find((c) => c.id === current.id) : undefined;
  const chosenVariant = education?.variants.find((v) => v.id === current.variant?.id);
  const limits = view.catalog?.steps[step];

  return (
    <div className="stack">
      <section className="card stack">
        <header className="row spread">
          <div>
            <p className="muted small step-kicker">{stepNames[id]}</p>
            <h3>
              {current.name}
              {current.variant && <span className="muted"> · {current.variant.name}</span>}
              {!current.useModule && <span className="badge">freie Erschaffung</span>}
              {current.individual && current.useModule && <span className="badge">angepasst</span>}
            </h3>
          </div>
          <span className="source">{page(current.source)}</span>
        </header>
        {current.summary && <p className="muted">{current.summary}</p>}
        <LongText value={current.description} language={language} />
        {culture && <CultureMap culture={culture} />}
        {education && education.variants.length > 0 && (
          <label className="field">
            <span>Variante der Ausbildung</span>
            <select value={current.variant?.id ?? ""} onChange={(e) => confirm({ type: "sm.setVariant", payload: { variant: e.target.value || null } }, "Variante wählen")}>
              <option value="">Grundmodul {education.name}</option>
              {education.variants.map((v) => {
                const conditions = [...v.cultures, ...v.races, ...(v.requirement ? [v.requirement] : [])];
                return (
                  <option key={v.id} value={v.id}>
                    {v.name} ({page(v.source)}){conditions.length > 0 ? ` – für ${conditions.join(", ")}` : ""}
                  </option>
                );
              })}
            </select>
          </label>
        )}
        {chosenVariant?.summary && <p className="muted small">{chosenVariant.summary}</p>}
        <div className="actions">
          <button type="button" className="link" onClick={() => setChoosing(true)}>
            {current.useModule ? "Anderes Modul wählen" : "Doch ein Modul wählen"}
          </button>
          {current.useModule && !current.individual && (
            <button type="button" className="link" onClick={() => void apply({ type: "sm.individualize", payload: { step } })}>
              Modul anpassen (Feinschliff, GRW S. 25)
            </button>
          )}
          {current.useModule && current.individual && (
            <button type="button" className="link" onClick={() => confirm({ type: "sm.resetToModule", payload: { step } }, "Zurück zum Modul")}>
              Anpassungen verwerfen
            </button>
          )}
        </div>
      </section>

      {current.useModule && !current.individual && current.slots && (
        <SlotChecklist step={id} slots={current.slots} choices={stepChoices} onReset={() => void apply({ type: "sm.clearChoices", payload: { step } })} />
      )}

      {(current.individual || !current.useModule) && (
        <section className="card stack">
          <h3>{current.useModule ? "Verteilung anpassen" : "Frei verteilen"}</h3>
          {limits && (
            <p className="muted small">
              {limits.skillPoints} Fertigkeitspunkte
              {limits.resourcePoints > 0 && `, ${limits.resourcePoints} Ressourcenpunkte`}
              {limits.strengthPoints > 0 && `, Stärken im Wert von ${limits.strengthPoints}`}
              {limits.masteries > 0 && `, ${limits.masteries} Meisterschaft${limits.masteries === 1 ? "" : "en"}`} (GRW S. 23–24).
              {current.useModule && " Was vom Modul abweicht, ist markiert."}
            </p>
          )}
          <AllocationEditor step={step} allocation={current.allocation} printed={current.printed} />
        </section>
      )}

      {(!current.useModule || current.individual) && stepChoices.length > 0 && <ExtraChoices choices={stepChoices} slots={null} />}
    </div>
  );
}

function ModulePicker({ step, current, onDone }: { step: ModuleStepId; current: StepView | null; onDone: () => void }) {
  const { view, confirm } = useSm();
  const catalog = view.catalog;
  const entries: CatalogEntry[] = !catalog ? [] : step === "kultur" ? catalog.cultures : step === "abstammung" ? catalog.ancestries : catalog.educations;
  let typical = new Set<string>();
  let typicalLabel = "";
  if (step === "kultur" && view.race) {
    typical = new Set(catalog?.cultures.filter((c) => c.typicalRaces.includes(view.race!.id)).map((c) => c.id));
    typicalLabel = `typisch für ${view.race.name}`;
  } else if (step === "abstammung" && view.culture?.id) {
    typical = new Set(catalog?.cultures.find((c) => c.id === view.culture?.id)?.typicalAncestries ?? []);
    typicalLabel = `typisch für ${view.culture.name}`;
  } else if (step === "ausbildung" && view.culture?.id) {
    typical = new Set(catalog?.cultures.find((c) => c.id === view.culture?.id)?.typicalEducations ?? []);
    typicalLabel = `typisch für ${view.culture.name}`;
  }
  const pick = (entry: CatalogEntry) => {
    onDone();
    window.scrollTo({ top: 0 });
    confirm({ type: "sm.setStep", payload: { step, id: entry.id, useModule: true } }, entry.name);
  };
  return (
    <div className="stack">
      <p className="muted small">
        Ein Modul füllt den Schritt mit den Werten aus dem Buch vor; einige Punkte bleiben zur Wahl. Du kannst das Modul später anpassen oder ganz frei verteilen.
        {typical.size > 0 && ` Module, die zu deiner bisherigen Wahl passen, stehen vorn.`}
      </p>
      {step === "kultur" ? (
        <CulturesByRegion cultures={catalog?.cultures ?? []} regions={catalog?.regions ?? []} selectedId={current?.id} typical={typical} typicalLabel={typicalLabel} onPick={pick} />
      ) : (
        <ModuleCards entries={entries} selectedId={current?.id} typical={typical} typicalLabel={typicalLabel} onPick={pick} />
      )}
      <section className="card row spread">
        <span className="muted small">Lieber ohne Modul? Dann verteilst du die Punkte des Schritts selbst innerhalb der Grenzen des GRW.</span>
        <span className="row">
          {current && (
            <button type="button" className="link" onClick={onDone}>
              Abbrechen
            </button>
          )}
          <button
            type="button"
            className="button secondary"
            onClick={() => {
              onDone();
              window.scrollTo({ top: 0 });
              confirm({ type: "sm.setStep", payload: { step, useModule: false, id: null } }, "Freie Erschaffung");
            }}
          >
            Frei erschaffen
          </button>
        </span>
      </section>
    </div>
  );
}

/** Where a culture stands in the list: a top-level region ("" for none) and possibly one of its sub-regions. */
interface CulturePlace {
  culture: CultureEntry;
  top: string;
  sub: string | null;
}

interface SubGroup {
  region: RegionEntry;
  cultures: CultureEntry[];
}

interface TopGroup {
  id: string;
  name: string;
  region: RegionEntry | null;
  direct: CultureEntry[];
  subs: SubGroup[];
  count: number;
}

/**
 * Cultures grouped by region (decisions E14, E39) on two levels: top-level regions, inside them first the cultures of the region itself,
 * then one group per sub-region. Groups and sub-groups with a typical culture come first (stable), cultures without a known region last.
 * A sub-region without a culture keeps its group (map and description) unless the user is searching.
 * A culture of several regions stands under each of them, but not again directly under a top-level region one of whose sub-regions it already stands under;
 * while searching it is shown once, under the first region path whose name matches, otherwise under its first path.
 */
function CulturesByRegion(props: {
  cultures: CultureEntry[];
  regions: RegionEntry[];
  selectedId?: string | null;
  typical: Set<string>;
  typicalLabel: string;
  onPick: (entry: CatalogEntry) => void;
}) {
  const { language } = useSm();
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState<CultureEntry | null>(null);
  const [shownRegion, setShownRegion] = useState<RegionEntry | null>(null);
  const needle = query.trim().toLowerCase();
  const byId = new Map(props.regions.map((r) => [r.id, r]));
  const isTop = (r: RegionEntry) => !r.parent || !byId.has(r.parent);
  const parentOf = (r: RegionEntry): string | null => (r.parent && byId.has(r.parent) && isTop(byId.get(r.parent)!) ? r.parent : null);
  const tops = props.regions.filter((r) => parentOf(r) === null);
  const matchesText = (text: string) => text.toLowerCase().includes(needle);
  const hasTypical = (cultures: CultureEntry[]) => cultures.some((c) => props.typical.has(c.id));
  const byTypical = <T,>(cultures: (g: T) => CultureEntry[]) => (a: T, b: T) => Number(hasTypical(cultures(b))) - Number(hasTypical(cultures(a)));

  const placed: CulturePlace[] = props.cultures.flatMap((c) => {
    const paths = [...new Set(c.regions)]
      .map((id) => byId.get(id))
      .filter((r): r is RegionEntry => !!r)
      .map((r) => {
        const parent = parentOf(r);
        return parent ? { top: parent, sub: r.id } : { top: r.id, sub: null };
      });
    const unique = paths.filter((p) => p.sub !== null || !paths.some((q) => q.top === p.top && q.sub !== null));
    if (!needle) return unique.length > 0 ? unique.map((p) => ({ culture: c, ...p })) : [{ culture: c, top: "", sub: null }];
    const pathName = (p: { top: string; sub: string | null }) => [byId.get(p.top)?.name ?? "", p.sub ? (byId.get(p.sub)?.name ?? "") : ""];
    const hit = unique.find((p) => pathName(p).some((n) => n !== "" && matchesText(n)));
    if (!hit && ![c.name, c.summary ?? ""].some(matchesText)) return [];
    return [{ culture: c, ...(hit ?? unique[0] ?? { top: "", sub: null }) }];
  });

  const distinct = (cultures: CultureEntry[]) => [...new Map(cultures.map((c) => [c.id, c])).values()];
  const groups: TopGroup[] = [
    ...tops.map((top) => {
      const here = placed.filter((p) => p.top === top.id);
      const direct = here.filter((p) => p.sub === null).map((p) => p.culture);
      const subs = props.regions
        .filter((r) => parentOf(r) === top.id)
        .map((region) => ({ region, cultures: here.filter((p) => p.sub === region.id).map((p) => p.culture) }))
        .filter((s) => s.cultures.length > 0 || !needle);
      return { id: top.id, name: top.name, region: top, direct, subs, count: distinct(here.map((p) => p.culture)).length };
    }),
    { id: "", name: "ohne Region", region: null, direct: placed.filter((p) => p.top === "").map((p) => p.culture), subs: [], count: placed.filter((p) => p.top === "").length },
  ]
    .filter((g) => g.count > 0)
    .sort(byTypical((g: TopGroup) => [...g.direct, ...g.subs.flatMap((s) => s.cultures)]));

  /** The cultures of the region itself (null) and the sub-region groups; the region's own come first unless only a sub-region has a typical culture. */
  const blocks = (group: TopGroup): (SubGroup | null)[] =>
    [...(group.direct.length > 0 ? [null] : []), ...group.subs].sort(byTypical((b: SubGroup | null) => (b === null ? group.direct : b.cultures)));
  const cards = (cultures: CultureEntry[], headingLevel: 4 | 5) => (
    <ModuleCards
      entries={cultures}
      selectedId={props.selectedId}
      typical={props.typical}
      typicalLabel={props.typicalLabel}
      headingLevel={headingLevel}
      details={(entry) => <CultureMapButton culture={entry} onShow={() => setShown(entry)} />}
      onPick={props.onPick}
    />
  );
  const regionActions = (region: RegionEntry) => (
    <span className="region-head-actions">
      <span className="source">{page(region.source)}</span>
      <RegionMapButton region={region} onShow={() => setShownRegion(region)} />
    </span>
  );

  return (
    <>
      <input type="search" placeholder="Kultur suchen …" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Kultur suchen" />
      {groups.length === 0 && <p className="muted">Keine Kultur gefunden.</p>}
      {groups.map((group) => (
        <section key={group.id} className="stack region-group" aria-label={`Region ${group.name}`}>
          <header className="row spread">
            <h3>
              {group.name} <span className="muted small">({group.count})</span>
            </h3>
            {group.region && regionActions(group.region)}
          </header>
          {group.region && <LongText value={group.region.description} language={language} label="Beschreibung der Region" />}
          {blocks(group).map((block, index) =>
            block === null ? (
              <div key="" className="stack region-direct">
                {index > 0 && <p className="muted small">Ohne Teilregion</p>}
                {cards(group.direct, 4)}
              </div>
            ) : (
              <section key={block.region.id} className="stack subregion-group" aria-label={`Teilregion ${block.region.name}`}>
                <header className="row spread">
                  <h4>
                    {block.region.name} <span className="muted small">({block.cultures.length})</span>
                  </h4>
                  {regionActions(block.region)}
                </header>
                <LongText value={block.region.description} language={language} label="Beschreibung der Region" />
                {block.cultures.length > 0 ? cards(block.cultures, 5) : <p className="muted small">Keine eigene Kultur.</p>}
              </section>
            ),
          )}
        </section>
      ))}
      {shown && <CultureMapSheet culture={shown} onClose={() => setShown(null)} />}
      {shownRegion && <RegionMapSheet region={shownRegion} onClose={() => setShownRegion(null)} />}
    </>
  );
}

/** The slots of the module, grouped by category; open ones expand one at a time so the user is led from decision to decision. */
function SlotChecklist(props: { step: ModuleStepId; slots: SlotView[]; choices: OpenChoice[]; onReset: () => void }) {
  const { apply } = useSm();
  const pending = props.slots.filter((s) => s.status === "open" || s.paramChoiceId);
  const firstOpen = pending[0]?.id ?? null;
  const [opened, setExpanded] = useState<string | null>(null);
  const expanded = opened !== null && pending.some((s) => s.id === opened) ? opened : firstOpen;
  const choiceById = new Map(props.choices.map((c) => [c.id, c]));
  const slotChoiceIds = new Set(props.slots.flatMap((s) => [s.choiceId, s.paramChoiceId]).filter((x): x is string => !!x));
  const extra = props.choices.filter((c) => !slotChoiceIds.has(c.id));
  const anyChosen = props.slots.some((s) => s.status === "chosen");

  return (
    <>
      <section className="card stack">
        <div className="row spread">
          <h3>Was das Modul bringt</h3>
          <span className={`badge ${pending.length === 0 ? "ok" : "warning"}`}>{pending.length === 0 ? "Alle Wahlen getroffen" : `Noch ${pending.length} ${pending.length === 1 ? "Wahl" : "Wahlen"} offen`}</span>
        </div>
        {categories.map((category) => {
          const slots = props.slots.filter((s) => s.category === category.id);
          if (slots.length === 0) return null;
          return (
            <div key={category.id}>
              <ListHeading>{category.label}</ListHeading>
              <List aria-label={category.label}>
                {slots.map((slot) => {
                  const choice = slot.choiceId ? choiceById.get(slot.choiceId) : undefined;
                  const paramChoice = slot.paramChoiceId ? choiceById.get(slot.paramChoiceId) : undefined;
                  const isOpen = slot.status === "open" || !!paramChoice;
                  const isExpanded = isOpen && expanded === slot.id;
                  const activeChoice = isExpanded ? (choice ?? paramChoice) : undefined;
                  return (
                    <li key={slot.id} className={`list-row slot ${slot.status}${isExpanded ? " expanded" : ""}`} data-slot={slot.id}>
                      <div className="list-tap static">
                        <span className="list-leading">
                          <span className={`mark ${slot.status === "fixed" ? "fixed" : isOpen ? "open" : "done"}`}>{isOpen ? "?" : "✓"}</span>
                        </span>
                        <span className="list-main">
                          <span className="list-title">
                            {slot.status === "open" ? describeOpen(slot) : slot.granted.join(", ")}
                            {slot.status === "chosen" && <span className="badge">gewählt</span>}
                            {paramChoice && <span className="badge warning">Angabe fehlt</span>}
                          </span>
                          {slot.status !== "open" && alternatives(slot).length > 0 && <span className="list-subtitle">statt {joinOr(alternatives(slot))}</span>}
                          {slot.note && <span className="list-subtitle">{slot.note}</span>}
                        </span>
                      </div>
                      <span className="list-actions">
                        {isOpen && !isExpanded && (
                          <button type="button" className="button secondary small" onClick={() => setExpanded(slot.id)}>
                            Wählen
                          </button>
                        )}
                        {slot.status === "chosen" && !paramChoice && slot.paramKey && (
                          <button type="button" className="link" onClick={() => void apply({ type: "sm.choose", payload: { step: props.step, slot: slot.id, key: slot.paramKey } })}>
                            Angabe ändern
                          </button>
                        )}
                        {slot.status === "chosen" && (
                          <button type="button" className="button secondary small" title="Wahl wieder öffnen" onClick={() => void apply({ type: "sm.choose", payload: { step: props.step, slot: slot.id } })}>
                            Bearbeiten
                          </button>
                        )}
                      </span>
                      {activeChoice && (
                        <div className="slot-choice">
                          <ChoiceCard choice={activeChoice} embedded />
                        </div>
                      )}
                    </li>
                  );
                })}
              </List>
            </div>
          );
        })}
        {anyChosen && (
          <div className="actions">
            <button type="button" className="link" onClick={props.onReset}>
              Alle Wahlen zurücksetzen
            </button>
          </div>
        )}
      </section>
      {extra.length > 0 && <ExtraChoices choices={extra} slots={props.slots} />}
    </>
  );
}

function ExtraChoices(props: { choices: OpenChoice[]; slots: SlotView[] | null }) {
  return (
    <section className="stack">
      <h3 className="section-title">Weitere Angaben zu diesem Schritt</h3>
      {props.choices.map((choice) => (
        <ChoiceCard key={choice.id} choice={choice} />
      ))}
    </section>
  );
}

/** The options of a decided slot that were not taken. */
function alternatives(slot: SlotView): string[] {
  return slot.options.length > 1 ? slot.options.filter((o) => !slot.granted.some((g) => g.includes(o))) : [];
}

/** What an open slot asks for, in one line. */
function describeOpen(slot: SlotView): string {
  const options = slot.optionGroup ? `${slot.optionGroup}: ${joinOr(slot.options)}` : joinOr(slot.options);
  const values = slot.values.map(signed);
  switch (slot.category) {
    case "fertigkeit":
      if (slot.kind === "pool") return `${slot.values[0] ?? 0} Punkte verteilen auf ${slot.options.join(", ")}`;
      if (slot.values.length > 1) return `Werte ${values.join(", ")} verteilen auf ${slot.options.join(", ")}`;
      return `${values[0] ?? ""} auf ${options}`;
    case "ressource":
      if (slot.values.length > 1) return `Werte ${values.join(", ")} verteilen auf ${slot.options.join(", ")}`;
      return `${values[0] ?? ""} auf ${options}`;
    case "staerke":
      return `Eine Stärke: ${options}`;
    case "meisterschaft":
      if (slot.options.length === 0) return "Eine Meisterschaft";
      return slot.optionGroup ? `Eine Meisterschaft in ${options}` : `Eine Meisterschaft in ${joinOr(slot.options)}`;
    default:
      return `Eine Sprache: ${options}`;
  }
}
